import cx from 'classnames';
import { Children } from 'react';
import type { FunctionComponent } from 'react';

import AccordionBody from '../Accordion/AccordionBody';
import useAccordion from '../Accordion/useAccordion';
import Icon, { icons } from '../Icon';

import Checkboxes from './FilterCardCheckboxes';
import MultiSelect from './FilterCardMultiSelect';
import Radios from './FilterCardRadios';
import TextInput from './FilterCardTextInput';

import css from './FilterCard.module.less';

type Props = {
  //  Unique identifier for this filter group. This is helpful for testing and accordion a11y.
  id: string;
  //   Title refers to the "title" section, which is the clickable zone at the top
  title: string;
};

type FilterCardType = FunctionComponent<Props> & {
  TextInput: typeof TextInput;
  MultiSelect: typeof MultiSelect;
  Checkboxes: typeof Checkboxes;
  Radios: typeof Radios;
};

/**
 * FilterCard displays a group of filters in a collapsable panel.
 */
const FilterCard: FilterCardType = ({
  id,
  title,
  children,
}) => {
  const { isOpen, targetProps, bodyProps } = useAccordion({ id });

  return (
    <aside className={css.filterCard}>
      <header className={cx(css.header, { [css.headerIsOpen]: isOpen })} {...targetProps}>
        <span className={cx(css.headerCarat, { [css.headerCaratOpen]: isOpen })}>
          <Icon icon={icons.caretRight} />
        </span>
        <span className={css.headerTitle}>
          {title}
        </span>
      </header>
      <AccordionBody
        {...bodyProps}
        isOpen={isOpen}
        className={css.body}
      >
        {Children.map(children, child => (
          //  Wrap each item in a class which applies consistent margin-bottom, creating vertical rhythm.
          <div className={css.bodyItem}>
            {child}
          </div>
        ))}
      </AccordionBody>
    </aside>
  );
};

FilterCard.TextInput = TextInput;
FilterCard.MultiSelect = MultiSelect;
FilterCard.Checkboxes = Checkboxes;
FilterCard.Radios = Radios;

export default FilterCard;
